import type { ScopesObject } from '@/shared/types';
import { removeMD5Hash } from '@/shared/functions';
import { evidenceCategoryScopeFieldsWithWireTokens } from '@/components/evidence-locker-ui-shared-new';

export type ScopedItemKind = 'file' | 'scope' | 'fileType';
export type ScopedItemState = 'included' | 'excluded';
export type ScopedItemGroup = 'included' | 'excluded' | 'fileType';

export type ScopedItem = {
  id: string;
  kind: ScopedItemKind;
  state: ScopedItemState;
  group: ScopedItemGroup;
  /** Basename for files, last path segment for folders, UI key for file types. */
  label: string;
  /** Full path, folder tag, or file-type key. */
  path: string;
};

function displaySegment(path: string): string {
  const segment = path.split('/').filter(Boolean).pop() || path;
  return removeMD5Hash(segment);
}

function fileOrScopeItem(
  path: string,
  kind: 'file' | 'scope',
  state: ScopedItemState,
): ScopedItem {
  return {
    id: `${kind}:${state}:${path}`,
    kind,
    state,
    group: state,
    label: displaySegment(path),
    path,
  };
}

function fileTypeItem(path: string, state: ScopedItemState): ScopedItem {
  return {
    id: `fileType:${state}:${path}`,
    kind: 'fileType',
    state,
    group: 'fileType',
    label: path,
    path,
  };
}

/** Included, excluded, then file-type items currently stored on a scope object. */
export function listScopedItems(scopes: ScopesObject): ScopedItem[] {
  return [
    ...(scopes.scopes ?? []).map((path) => fileOrScopeItem(path, 'scope', 'included')),
    ...(scopes.files ?? []).map((path) => fileOrScopeItem(path, 'file', 'included')),
    ...(scopes.excludedScopes ?? []).map((path) => fileOrScopeItem(path, 'scope', 'excluded')),
    ...(scopes.excludedFiles ?? []).map((path) => fileOrScopeItem(path, 'file', 'excluded')),
    ...(scopes.evidenceCategoryScopes ?? []).map((path) => fileTypeItem(path, 'included')),
    ...(scopes.excludedEvidenceCategoryScopes ?? []).map((path) => fileTypeItem(path, 'excluded')),
  ];
}

export function hasScopedItems(scopes: ScopesObject): boolean {
  return listScopedItems(scopes).length > 0;
}

export function clearScopedItems(scopes: ScopesObject): ScopesObject {
  return {
    ...scopes,
    scopes: [],
    files: [],
    excludedScopes: [],
    excludedFiles: [],
    contentCategories: [],
    excludedContentCategories: [],
    evidenceCategoryChips: [],
    evidenceCategoryScopes: [],
    excludedEvidenceCategoryScopes: [],
    evidenceAllFileTypes: false,
  };
}

/**
 * Drop one scoped item. File-type removal also refreshes the wire
 * ``contentCategories`` fields the timeline builder sends.
 */
export function removeScopedItem(
  scopes: ScopesObject,
  item: Pick<ScopedItem, 'kind' | 'path'>,
): ScopesObject {
  const path = item.path;
  if (item.kind === 'file') {
    if (scopes.files?.includes(path)) {
      return { ...scopes, files: scopes.files.filter((entry) => entry !== path) };
    }
    if (scopes.excludedFiles?.includes(path)) {
      return {
        ...scopes,
        excludedFiles: scopes.excludedFiles.filter((entry) => entry !== path),
      };
    }
    return scopes;
  }
  if (item.kind === 'scope') {
    if (scopes.scopes?.includes(path)) {
      return { ...scopes, scopes: scopes.scopes.filter((entry) => entry !== path) };
    }
    if (scopes.excludedScopes?.includes(path)) {
      return {
        ...scopes,
        excludedScopes: scopes.excludedScopes.filter((entry) => entry !== path),
      };
    }
    return scopes;
  }
  const included = (scopes.evidenceCategoryScopes ?? []).filter((entry) => entry !== path);
  const excluded = (scopes.excludedEvidenceCategoryScopes ?? []).filter((entry) => entry !== path);
  return {
    ...scopes,
    ...evidenceCategoryScopeFieldsWithWireTokens(
      included,
      excluded,
      !!scopes.evidenceAllFileTypes,
    ),
  };
}
